import {z} from '@hono/zod-openapi';

import {hondaMonthlyReportRequestSchema} from './hondaMonthlyReportRequestSchema';
import {hondaReportDateSchema} from './hondaReportDateSchema';

const identifierSchema = z.string().trim().min(1);
const repairSchema = hondaMonthlyReportRequestSchema.shape.repairHistory.element;

const moneySchema = z
  .string()
  .max(100)
  .refine((value) => {
    const unsigned = value.startsWith('-') ? value.slice(1) : value;
    const pieces = unsigned.split('.');
    return pieces.length <= 2 && pieces.every((piece) => /^[0-9]+$/.test(piece));
  }, 'Expected a decimal amount');

/**
 * Normalized, branch-scoped DMS facts used to construct the Honda monthly report.
 * The source adapter resolves configuration and maps order variants explicitly;
 * it must never derive Honda categories from editable names.
 */
export const hondaMonthlyReportFactsSchema = z
  .object({
    tenantId: identifierSchema,
    branchId: identifierSchema,
    configuration: hondaMonthlyReportRequestSchema
      .pick({
        dealerCode: true,
        dmsVersion: true,
        employeeCounts: true,
        facilityParams: true,
      })
      .extend({
        currency: z.enum(['CZK', 'EUR']),
        hourlyRates: z.array(
          hondaMonthlyReportRequestSchema.shape.labourSummary.innerType().element.pick({
            orderType: true,
            hourlyRate: true,
          })
        ),
      }),
    vehicles: z.array(
      z.object({
        id: identifierSchema,
        vin: repairSchema.shape.vin,
        registeredAt: hondaReportDateSchema,
        currentCustomerId: identifierSchema,
        warranty: z
          .object({start: hondaReportDateSchema, endInclusive: hondaReportDateSchema})
          .refine((value) => value.start <= value.endInclusive, 'Invalid warranty interval')
          .nullable(),
      })
    ),
    visits: z.array(
      z.object({
        id: identifierSchema,
        vehicleId: identifierSchema,
        customerId: identifierSchema,
        completedAt: hondaReportDateSchema,
        odometer: z.number().int().nonnegative().nullable(),
      })
    ),
    orders: z.array(
      z.object({
        id: identifierSchema,
        vehicleId: identifierSchema,
        completedAt: hondaReportDateSchema,
        currency: z.enum(['CZK', 'EUR']),
        repair: repairSchema,
        items: z.array(
          z.object({
            id: identifierSchema,
            kind: z.enum(['ownLabour', 'externalLabour', 'genuineHondaPart', 'otherPart']),
            amountExcludingVat: moneySchema,
            billedHours: moneySchema,
          })
        ),
      })
    ),
    counterSales: z.array(
      z.object({
        id: identifierSchema,
        invoicedAt: hondaReportDateSchema,
        currency: z.enum(['CZK', 'EUR']),
        genuineHonda: z.boolean(),
        internalIssue: z.boolean(),
        quantity: z.number().int(),
        amountExcludingVat: moneySchema,
      })
    ),
  })
  .openapi('HondaMonthlyReportFacts');

export type HondaMonthlyReportFacts = z.infer<typeof hondaMonthlyReportFactsSchema>;
